import type { JobOfferTerms } from "../../lib/api-client/types/employment";
import { JobOfferSchedule } from "../../components/JobOfferTerms";
import { JourneyProgress } from "../../components/ContractJourney";
import type { Engagement } from "../../lib/api-client/contracts";

export function EngagementMilestones({ terms, salary, paymentRequired = true }: { terms: JobOfferTerms | null; salary: number | null; paymentRequired?: boolean }) {
  if (!paymentRequired) return <section aria-label="Job milestones"><h2 className="font-bold text-navy">Milestones</h2><p className="mt-2 text-sm text-mut">No payment milestones are recorded for this earlier hire.</p></section>;
  return <section aria-label="Accepted monthly milestones">
    <h2 className="font-bold text-navy">Monthly milestones</h2>
    <p className="mt-1 text-sm text-mut">Accepted offer schedule. Payment milestones are finalized when the Employment version is sent; release requires signatures and funding.</p>
    {terms && salary != null ? <JobOfferSchedule startDate={terms.startDate} months={terms.months} salary={salary} expanded />
      : <p className="mt-3 text-sm text-mut">This earlier hire has no recorded offer schedule. Review the Employment agreement for its payment terms.</p>}
  </section>;
}

/** Earlier unlinked hires retain their recorded nonfinancial lifecycle; the shared
 * process guide must not imply salary consent, signatures or escrow history. */
export function LegacyEngagementJourney({ status }: { status: Engagement["status"] }) {
  return <JourneyProgress title="Job contract process" current={status === "completed" ? 3 : status === "cancelled" ? -1 : 1}
    outcome={status === "completed" ? "Complete" : status === "cancelled" ? "Cancelled" : undefined}
    notice={status === "cancelled" ? "This work record was cancelled. The remaining steps no longer apply." : undefined}
    steps={[
      { label: "Hire confirmed", detail: "Job engagement recorded." },
      { label: "Work & updates", detail: "Record progress with the shared checklist and reports." },
      { label: "Complete", detail: "Either party can complete the work record." },
    ]} />;
}

/** The engagement has no financial contract yet. Show the same process presentation
 * with preparation still required, without inventing signatures or funded stages. */
export function EngagementJourney({ employer, onOpenAgreement }: { employer: boolean; onOpenAgreement: () => void }) {
  return <div>
    <JourneyProgress title="Job contract process" current={1} steps={[
      { label: "Hire confirmed", detail: "Monthly salary accepted." },
      { label: "Sign agreement", detail: "Review the Employment agreement and sign. Both parties must sign before funding." },
      { label: employer ? "Fund full contract" : "Escrow funding", detail: employer ? "Fund all monthly milestones after both parties sign." : "Await full contract funding after both parties sign." },
      { label: employer ? "Release monthly payment" : "Record work", detail: "Monthly releases are manual. Work updates do not release payment." },
      { label: "Complete", detail: "All monthly milestones released." },
    ]} />
    <div className="mt-3 flex justify-end"><button type="button" className="btn-outline" onClick={onOpenAgreement}>Open Agreement</button></div>
  </div>;
}
